/**
 * DashboardConfigPage (ED - Enroller Admin)
 * Split-view: dashboard list on the left, Metabase iframe on the right.
 * Mirrors the UX of the user-roles Analytics page (UserDashboardPage).
 * Supports Add / Edit / Delete and filtering.
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus,
  Loader2,
  Search,
  X,
  Menu,
  ChevronLeft,
  RefreshCw,
  AlertCircle,
  ExternalLink,
  BarChart2,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import {
  useDashboardConfigs,
  useDeleteDashboardConfig,
  normaliseDashboardItem,
} from '@/hooks/dashboardConfig/useDashboardConfig';
import { getDashboardItem } from '@/services/dashboardConfigApi';
import { enrollerAdminService } from '@/services/ea/enrollerAdminService';
import { DashboardConfigModal } from './components/DashboardConfigModal';
import { DashboardConfigList } from './components/DashboardConfigList';
import type { NormalisedDashboardItem } from '@/types/dashboardConfig';

const REFRESH_BUFFER_MS = 60_000;
const METABASE_APP_BASE_URL = 'https://analytics.hbox.ai';

const DashboardConfigPage = () => {
  useEffect(() => {
    document.title = 'Dashboard | Admin';
  }, []);

  const { user, token } = useAuth();
  const loginUserId = user?.id ?? null;

  const { items, isLoading: itemsLoading, refetch: refetchItems } = useDashboardConfigs(loginUserId, 'ED');
  const { remove } = useDeleteDashboardConfig(loginUserId);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  // ─── Filter state ───────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');

  const availableRoles = useMemo(() => {
    const roleSet = new Set<string>();
    items.forEach((item) => item.userTypes.forEach((r) => roleSet.add(r)));
    // If any item has no userTypes (null), surface EA so it can be filtered to
    if (items.some((item) => item.userTypes.length === 0)) {
      roleSet.add('EA');
    }
    return Array.from(roleSet).sort();
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch =
        !searchQuery.trim() ||
        item.customName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.metabaseQuestionName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole =
        selectedRole === 'all' ||
        item.userTypes.includes(selectedRole) ||
        // Items with no assigned roles only surface under the EA filter
        (selectedRole === 'EA' && item.userTypes.length === 0);
      return matchesSearch && matchesRole;
    });
  }, [items, searchQuery, selectedRole]);

  const hasActiveFilters = searchQuery !== '' || selectedRole !== 'all';

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [filteredItems]);

  // ─── Modal state ──────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<NormalisedDashboardItem | null>(null);
  const [fetchingEditId, setFetchingEditId] = useState<number | null>(null);

  const handleAdd = () => {
    setEditItem(null);
    setShowModal(true);
  };

  const handleEdit = useCallback(async (item: NormalisedDashboardItem) => {
    if (!loginUserId || !token) {
      setEditItem(item);
      setShowModal(true);
      return;
    }
    setFetchingEditId(item.id);
    try {
      const fresh = await getDashboardItem(loginUserId, token, item.id);
      setEditItem(normaliseDashboardItem(fresh));
    } catch {
      setEditItem(item);
    } finally {
      setFetchingEditId(null);
      setShowModal(true);
    }
  }, [loginUserId, token]);

  const handleDelete = async (item: NormalisedDashboardItem) => {
    setDeletingId(item.id);
    const result = await remove(item.id);
    setDeletingId(null);
    if (result.success) {
      toast({
        title: 'Dashboard deleted',
        description: `"${item.customName}" has been removed.`,
      });
      if (selected?.id === item.id) {
        setSelected(null);
        setIframeUrl(null);
      }
      refetchItems();
    } else {
      toast({
        title: 'Failed to delete',
        description: result.error ?? 'Unknown error occurred.',
        variant: 'destructive',
      });
    }
  };

  const handleModalSuccess = () => {
    refetchItems();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedRole('all');
  };

  // ─── Split-view state ─────────────────────────────────────────────────────
  const [listCollapsed, setListCollapsed] = useState(false);
  const [selected, setSelected] = useState<NormalisedDashboardItem | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeLoading, setIframeLoading] = useState(false);
  const [iframeError, setIframeError] = useState<string | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  useEffect(
    () => () => {
      clearRefreshTimer();
      abortRef.current?.abort();
    },
    [clearRefreshTimer]
  );

  const loadIframe = useCallback(
    async (dashItem: NormalisedDashboardItem) => {
      if (!user || !token) return;
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      clearRefreshTimer();
      setIframeUrl((prev) => {
        if (prev) setIframeKey((k) => k + 1);
        return null;
      });
      setIframeError(null);
      setIframeLoading(true);
      enrollerAdminService.updateContext({ userId: user.id, token });

      try {
        const response = await enrollerAdminService.getMetabaseToken({
          type: dashItem.resourceType ?? 'dashboard',
          id: dashItem.resourceId,
          user_email: user.email ?? '',
          filters: dashItem.dashboardFilters ?? [],
          userContext: {
            userId: user.id,
            userEmail: user.email,
            userFullName: user.fullName ?? '',
          },
        });
        if (controller.signal.aborted) return;
        if (response?.token) {
          const url = enrollerAdminService.buildMetabaseEmbedUrl(
            dashItem.resourceType ?? 'dashboard',
            response.token
          );
          setIframeUrl(url);
          if (response.expires_at_epoch_ms) {
            const delay = response.expires_at_epoch_ms - Date.now() - REFRESH_BUFFER_MS;
            if (delay > 0) {
              refreshTimerRef.current = setTimeout(() => loadIframe(dashItem), delay);
            }
          }
        } else {
          setIframeError('No token returned from server');
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setIframeError(err instanceof Error ? err.message : 'Failed to load dashboard');
      } finally {
        if (!controller.signal.aborted) setIframeLoading(false);
      }
    },
    [user, token, clearRefreshTimer]
  );

  const handleCardClick = useCallback(
    (item: NormalisedDashboardItem) => {
      setSelected(item);
      loadIframe(item);
    },
    [loadIframe]
  );

  // Auto-select first (default) dashboard once items load
  useEffect(() => {
    if (itemsLoading || items.length === 0 || selected) return;
    const sorted = [...items].sort((a, b) => {
      if (a.isDefault !== b.isDefault) return a.isDefault ? -1 : 1;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    handleCardClick(sorted[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsLoading, items]);

  const refreshIframe = useCallback(() => {
    if (selected) loadIframe(selected);
  }, [selected, loadIframe]);

  const openSelectedInMetabase = useCallback(() => {
    if (!selected) return;
    const path = selected.resourceType === 'question' ? 'question' : 'dashboard';
    const targetUrl = `${METABASE_APP_BASE_URL}/${path}/${selected.resourceId}`;
    window.open(targetUrl, '_blank', 'noopener,noreferrer');
  }, [selected]);

  return (
    <div className="space-y-4 flex flex-col h-[calc(100vh-120px)] overflow-hidden">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0"
            onClick={() => setListCollapsed((v) => !v)}
            title={listCollapsed ? 'Show dashboards list' : 'Hide dashboards list'}
            aria-label={listCollapsed ? 'Show dashboards list' : 'Hide dashboards list'}
          >
            {listCollapsed ? <Menu className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground truncate">Dashboard</h1>
            {itemsLoading && (
              <p className="text-sm text-muted-foreground mt-0.5">Loading…</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {selected && iframeUrl && !iframeLoading && (
            <>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={refreshIframe}>
                <RefreshCw className="h-3.5 w-3.5" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={openSelectedInMetabase}
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open in Metabase
              </Button>
            </>
          )}
          <Button size="sm" onClick={handleAdd} disabled={fetchingEditId !== null}>
            <Plus className="h-4 w-4 mr-1.5" />
            Add Analytics Dashboard
          </Button>
        </div>
      </div>

      {/* ── Fetching-edit indicator ─────────────────────────────────────────── */}
      {fetchingEditId !== null && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading dashboard details…
        </div>
      )}

      {/* ── Split body ─────────────────────────────────────────────────────── */}
      {!itemsLoading && items.length === 0 ? (
        <div className="flex-1 min-h-0 flex items-center justify-center">
          <DashboardConfigList
            items={filteredItems}
            itemsLoading={itemsLoading}
            deletingId={deletingId}
            fetchingEditId={fetchingEditId}
            onCardClick={handleCardClick}
            onEdit={handleEdit}
            onDelete={handleDelete}
            compact
            selectedId={selected?.id ?? null}
          />
        </div>
      ) : (
      <div
        className={`grid grid-cols-1 gap-4 flex-1 min-h-0 overflow-hidden ${
          listCollapsed ? 'lg:grid-cols-[72px_1fr]' : 'lg:grid-cols-[340px_1fr]'
        }`}
      >
        {/* Left: list */}
        {!listCollapsed ? (
          <div className="overflow-y-auto pl-2 pr-1 pt-2 min-h-0 space-y-3">
            {!itemsLoading && items.length > 0 && (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex flex-1 min-w-[200px] items-center overflow-hidden rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring focus-within:ring-offset-0">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search dashboards…"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-9 border-0 pl-10 pr-3 text-sm shadow-none focus-visible:ring-0"
                    />
                  </div>
                  <div className="h-5 w-px bg-border" />
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger
                      className={`h-9 rounded-none border-0 bg-transparent text-sm shadow-none focus:ring-0 focus:ring-offset-0 ${
                        selectedRole === 'all' ? 'w-[116px]' : 'w-[76px]'
                      }`}
                    >
                      <SelectValue placeholder="All Roles" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {availableRoles.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasActiveFilters && (
                    <>
                      <div className="h-5 w-px bg-border" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={clearFilters}
                        className="h-9 w-9 shrink-0 rounded-none border-0"
                        aria-label="Clear filters"
                        title="Clear filters"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}

            <DashboardConfigList
              items={filteredItems}
              itemsLoading={itemsLoading}
              deletingId={deletingId}
              fetchingEditId={fetchingEditId}
              onCardClick={handleCardClick}
              onEdit={handleEdit}
              onDelete={handleDelete}
              compact
              selectedId={selected?.id ?? null}
            />
          </div>
        ) : (
          <div className="min-h-0 overflow-y-auto pt-2">
            <Card className="border-border/70">
              <CardContent className="p-2 flex flex-col items-center gap-2">
                {sortedItems.map((item) => {
                  const isActive = selected?.id === item.id;
                  return (
                    <Button
                      key={item.id}
                      variant={isActive ? 'default' : 'ghost'}
                      className={`h-10 w-10 p-0 ${
                        isActive ? 'bg-primary text-primary-foreground' : 'border border-border'
                      }`}
                      title={item.customName}
                      onClick={() => handleCardClick(item)}
                    >
                      <BarChart2 className="h-4 w-4" />
                    </Button>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Right: iframe */}
        <div className="min-w-0 min-h-0 flex flex-col overflow-hidden pt-2">
          {!selected && !iframeLoading && (
            <div className="flex items-center justify-center py-24 border border-border rounded-lg text-muted-foreground text-sm">
              Select a dashboard to view
            </div>
          )}

          {selected && (
            <div className="flex flex-col flex-1 min-h-0 gap-3">
              {iframeLoading && (
                <div className="flex items-center justify-center py-24 bg-muted/40 rounded-lg text-muted-foreground">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Loading dashboard…
                </div>
              )}

              {iframeError && !iframeLoading && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="flex items-center justify-between gap-4">
                    <span>{iframeError}</span>
                    <Button size="sm" variant="outline" className="shrink-0" onClick={refreshIframe}>
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              )}

              {iframeUrl && !iframeLoading && !iframeError && (
                <Card className="flex-1 min-h-0 flex flex-col">
                  <CardContent className="p-0 rounded-lg flex-1 min-h-0 overflow-auto">
                    <iframe
                      key={`${iframeUrl}-${iframeKey}`}
                      src={iframeUrl}
                      width="100%"
                      className="block w-full h-[calc(100vh-200px)] max-h-[calc(100vh-200px)] min-h-[600px]"
                      title={selected.customName}
                      allowFullScreen
                      scrolling="auto"
                    />
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Create / Edit Modal ───────────────────────────────────────────────── */}
      <DashboardConfigModal
        open={showModal}
        loginUserId={loginUserId}
        initialItem={editItem}
        onClose={() => { setShowModal(false); setEditItem(null); }}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default DashboardConfigPage;
